<template>
  <div class="vivo">
    <div v-if="loading" class="state">{{ $t("chatbot-qa.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("chatbot-qa.error") }}</div>
    <template v-else>
      <!-- page head + primary add -->
      <div class="row between" style="margin-bottom: 14px">
        <div>
          <div class="pagetitle">{{ $t("chatbot-qa.title") }}</div>
          <div class="crumb">{{ $t("common.company") }} / {{ $t("chatbot-qa.crumb") }}</div>
        </div>
        <button class="btn primary" type="button" @click="startCreate">{{ $t("chatbot-qa.addBtn") }}</button>
      </div>

      <div class="callout" style="margin-bottom: 14px">ℹ️ {{ $t("chatbot-qa.reference") }}</div>

      <div class="grid2">
        <!-- ===== left column: list + add/edit form ===== -->
        <div>
          <div class="panel">
            <div class="panel__head">
              <h4>{{ $t("chatbot-qa.listTitle") }}</h4>
              <span class="tag muted">{{ $t("chatbot-qa.count", { n: fInt(total) }) }}</span>
            </div>
            <div class="toolbar">
              <div class="searchbar grow">
                🔎
                <input
                  class="input"
                  style="border: 0; background: transparent; box-shadow: none; padding: 0"
                  :placeholder="$t('chatbot-qa.searchPlaceholder')"
                  v-model="q"
                />
              </div>
            </div>
            <div class="table-wrap">
              <table class="table">
                <thead>
                  <tr>
                    <th>{{ $t("chatbot-qa.cols.question") }}</th>
                    <th>{{ $t("chatbot-qa.cols.phrasings") }}</th>
                    <th>{{ $t("chatbot-qa.cols.status") }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="rows.length === 0">
                    <td colspan="4" class="muted" style="text-align: center; padding: 22px">
                      {{ $t("chatbot-qa.empty") }}
                    </td>
                  </tr>
                  <tr v-for="r in rows" :key="r.id">
                    <td>{{ r.question }}</td>
                    <td><span class="tag muted">{{ $t("chatbot-qa.phrasingCount", { n: fInt(r.phrasingCount) }) }}</span></td>
                    <td>
                      <span class="tag" :class="statusTag(r.statusKey)">
                        <span class="dotc"></span> {{ statusLabel(r.statusKey) }}
                      </span>
                    </td>
                    <td>
                      <div class="row" style="gap: 6px">
                        <button class="btn ghost sm" type="button" @click="startEdit(r)">{{ $t("chatbot-qa.act.edit") }}</button>
                        <button
                          v-if="r.isActive"
                          class="btn danger sm"
                          type="button"
                          @click="toggleActive(r)"
                        >
                          {{ $t("chatbot-qa.act.suspend") }}
                        </button>
                        <button v-else class="btn ok sm" type="button" @click="toggleActive(r)">
                          {{ $t("chatbot-qa.act.activate") }}
                        </button>
                        <button class="btn ghost sm" type="button" @click="removeRow(r)">{{ $t("chatbot-qa.act.delete") }}</button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- add / edit form -->
          <div class="panel mt2">
            <div class="panel__head">
              <h4>{{ editing ? $t("chatbot-qa.form.editTitle") : $t("chatbot-qa.form.createTitle") }}</h4>
              <button class="btn primary sm" type="button" :disabled="saving" @click="save">{{ $t("chatbot-qa.form.save") }}</button>
            </div>
            <div class="panel__body">
              <div class="form">
                <div class="field full">
                  <label>{{ $t("chatbot-qa.field.question") }} <span class="req">*</span></label>
                  <input
                    class="input"
                    :class="{ err: formError }"
                    :placeholder="$t('chatbot-qa.field.questionPlaceholder')"
                    v-model="form.question"
                  />
                </div>
                <div class="field full">
                  <label>{{ $t("chatbot-qa.field.answer") }} <span class="req">*</span></label>
                  <textarea
                    class="textarea"
                    :class="{ err: formError }"
                    rows="3"
                    :placeholder="$t('chatbot-qa.field.answerPlaceholder')"
                    v-model="form.answer"
                  ></textarea>
                </div>
                <div class="field full">
                  <label>{{ $t("chatbot-qa.field.phrasings") }}</label>
                  <div class="row" style="gap: 6px; flex-wrap: wrap; margin-bottom: 8px">
                    <span v-for="(p, i) in form.phrasings" :key="i" class="chip on">
                      {{ p }}
                      <button
                        type="button"
                        style="border: 0; background: transparent; color: inherit; cursor: pointer; padding: 0 0 0 4px"
                        @click="removePhrasing(i)"
                      >✕</button>
                    </span>
                    <span v-if="form.phrasings.length === 0" class="muted xs">—</span>
                  </div>
                  <div class="row" style="gap: 6px">
                    <input
                      class="input"
                      :placeholder="$t('chatbot-qa.field.phrasingPlaceholder')"
                      v-model="newPhrasing"
                      @keyup.enter="addPhrasing"
                    />
                    <button class="chip" type="button" @click="addPhrasing">{{ $t("chatbot-qa.field.addPhrasing") }}</button>
                  </div>
                </div>
                <div class="field">
                  <label>{{ $t("chatbot-qa.field.category") }}</label>
                  <select class="select" v-model="form.category">
                    <option v-for="c in categoryOptions" :key="c" :value="c">{{ c }}</option>
                  </select>
                </div>
                <div class="field">
                  <label>{{ $t("chatbot-qa.field.status") }}</label>
                  <select class="select" v-model="form.isActive">
                    <option :value="true">{{ $t("chatbot-qa.status.active") }}</option>
                    <option :value="false">{{ $t("chatbot-qa.status.inactive") }}</option>
                  </select>
                </div>
              </div>
              <div v-if="formError" class="muted xs" style="color: var(--v-danger); margin-top: 8px">
                {{ formErrorMsg }}
              </div>
              <div class="divider"></div>
              <div class="row" style="gap: 10px">
                <button class="btn primary" type="button" :disabled="saving" @click="save">{{ $t("chatbot-qa.form.save") }}</button>
                <button v-if="editing" class="btn ghost" type="button" @click="cancelForm">{{ $t("chatbot-qa.form.cancel") }}</button>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== right column: test-ask chat ===== -->
        <div>
          <div class="panel">
            <div class="panel__head">
              <h4>{{ $t("chatbot-qa.preview.title") }}</h4>
              <span class="tag purple">{{ $t("chatbot-qa.preview.tryTag") }}</span>
            </div>
            <div class="panel__body">
              <div class="row" style="gap: 6px; margin-bottom: 12px">
                <input
                  class="input"
                  :placeholder="$t('chatbot-qa.preview.askPlaceholder')"
                  v-model="askQuestion"
                  @keyup.enter="runAsk"
                />
                <button class="btn primary" type="button" :disabled="asking" @click="runAsk">{{ $t("chatbot-qa.preview.askBtn") }}</button>
              </div>

              <div v-if="askError" class="muted xs" style="color: var(--v-danger); margin-bottom: 8px">
                {{ $t("chatbot-qa.preview.error") }}
              </div>

              <div v-if="askResult" class="chat">
                <div class="bubble me">{{ askedText }}</div>
                <div v-if="askResult.matched" class="xs muted" style="text-align: center">
                  {{ $t("chatbot-qa.preview.matchedLabel") }} «{{ matchedQuestion }}»
                </div>
                <div class="bubble bot">
                  {{ askResult.matched ? askResult.answer : $t("chatbot-qa.preview.fallback") }}
                </div>
                <div class="row" style="gap: 8px; justify-content: center">
                  <span class="tag" :class="askResult.matched ? 'ok' : 'muted'">
                    {{ $t("chatbot-qa.preview.sourceLabel") }}: {{ sourceLabel(askResult.source) }}
                  </span>
                  <span class="tag purple mono">{{ $t("chatbot-qa.preview.confidence", { n: fInt(askResult.confidencePct) }) }}</span>
                </div>
              </div>

              <div class="note info mt">
                <b>{{ $t("chatbot-qa.orderNoteLabel") }}</b>{{ $t("chatbot-qa.orderNote") }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet, apiPost, apiPatch, apiDelete } from "../../api/vivo/http";
import { statusOf, phrasingCount, filterQa, shapeAsk } from "./chatbot-qa-vm";

function emptyForm() {
  return { id: null, question: "", answer: "", category: "", isActive: true, phrasings: [] };
}

const STATUS_TAG = {
  active: "ok",
  inactive: "muted",
};

export default {
  name: "VivoChatbotQa",
  data() {
    return {
      items: [],
      total: 0,
      q: "",
      loading: true,
      error: false,
      // add / edit form
      form: emptyForm(),
      newPhrasing: "",
      editing: false,
      saving: false,
      formError: false,
      formErrorMsg: "",
      // test-ask
      askQuestion: "",
      askedText: "",
      askResult: null,
      asking: false,
      askError: false,
    };
  },
  async mounted() {
    try {
      await this.reload();
      this.startCreate();
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  computed: {
    enriched() {
      return this.items.map((qa) => ({
        id: qa.id,
        question: qa.question,
        answer: qa.answer,
        category: qa.category,
        isActive: qa.isActive !== false,
        phrasings: qa.phrasings || [],
        phrasingCount: phrasingCount(qa),
        statusKey: statusOf(qa),
      }));
    },
    rows() {
      return filterQa(this.enriched, this.q);
    },
    categoryOptions() {
      const base = [
        this.$t("chatbot-qa.categories.internal"),
        this.$t("chatbot-qa.categories.policy"),
        this.$t("chatbot-qa.categories.network"),
        this.$t("chatbot-qa.categories.general"),
      ];
      const cur = String(this.form.category || "").trim();
      if (cur && base.indexOf(cur) === -1) return [cur].concat(base);
      return base;
    },
    matchedQuestion() {
      if (!this.askResult || !this.askResult.matchedQaId) return "";
      const m = this.items.find((x) => x.id === this.askResult.matchedQaId);
      return m ? m.question : "";
    },
  },
  methods: {
    fInt(n) {
      return new Intl.NumberFormat(this.$i18n.locale, { numberingSystem: "latn" }).format(n || 0);
    },
    statusLabel(key) {
      return this.$t("chatbot-qa.status." + key);
    },
    statusTag(key) {
      return STATUS_TAG[key] || "muted";
    },
    sourceLabel(source) {
      return this.$t("chatbot-qa.source." + source);
    },
    async reload() {
      const res = await apiGet("/chatbot-qa", { scope: "company" });
      this.items = (res && res.data) || [];
      this.total = res && res.meta ? res.meta.total : this.items.length;
    },
    startCreate() {
      this.form = emptyForm();
      this.form.category = this.$t("chatbot-qa.categories.internal");
      this.newPhrasing = "";
      this.editing = false;
      this.formError = false;
    },
    startEdit(row) {
      this.form = {
        id: row.id,
        question: row.question,
        answer: row.answer,
        category: row.category || this.$t("chatbot-qa.categories.internal"),
        isActive: row.isActive !== false,
        phrasings: (row.phrasings || []).slice(),
      };
      this.newPhrasing = "";
      this.editing = true;
      this.formError = false;
    },
    cancelForm() {
      this.startCreate();
    },
    addPhrasing() {
      const p = String(this.newPhrasing || "").trim();
      if (!p) return;
      if (this.form.phrasings.indexOf(p) === -1) this.form.phrasings.push(p);
      this.newPhrasing = "";
    },
    removePhrasing(i) {
      this.form.phrasings.splice(i, 1);
    },
    async save() {
      const question = String(this.form.question || "").trim();
      const answer = String(this.form.answer || "").trim();
      if (!question || !answer) {
        this.formError = true;
        this.formErrorMsg = this.$t("chatbot-qa.required");
        return;
      }
      this.formError = false;
      this.saving = true;
      // `scope` is a QUERY param, not a body field (CreateChatbotQaDto has no
      // `scope` → forbidNonWhitelisted 400), and the service REQUIRES ?scope= on
      // every mutation. Mirrors the GET list (reload) which already scopes.
      const body = {
        question: question,
        answer: answer,
        category: String(this.form.category || "").trim(),
        isActive: !!this.form.isActive,
        phrasings: this.form.phrasings.slice(),
      };
      try {
        if (this.editing && this.form.id) {
          await apiPatch("/chatbot-qa/" + this.form.id + "?scope=company", body);
        } else {
          await apiPost("/chatbot-qa?scope=company", body);
        }
        this.startCreate();
        await this.reload();
      } catch (e) {
        this.formError = true;
        this.formErrorMsg = this.$t("chatbot-qa.saveError");
      } finally {
        this.saving = false;
      }
    },
    async toggleActive(row) {
      try {
        await apiPatch("/chatbot-qa/" + row.id + "?scope=company", {
          isActive: !row.isActive,
        });
        await this.reload();
      } catch (e) {
        this.error = true;
      }
    },
    async removeRow(row) {
      try {
        await apiDelete("/chatbot-qa/" + row.id + "?scope=company");
        if (this.editing && this.form.id === row.id) this.startCreate();
        await this.reload();
      } catch (e) {
        this.error = true;
      }
    },
    async runAsk() {
      const question = String(this.askQuestion || "").trim();
      if (!question) return;
      this.asking = true;
      this.askError = false;
      try {
        const res = await apiPost("/chatbot-qa/ask", { question: question });
        this.askedText = question;
        this.askResult = shapeAsk(res);
      } catch (e) {
        this.askError = true;
      } finally {
        this.asking = false;
      }
    },
  },
};
</script>
